import { useEffect, useState } from "react";

const SETTINGS_FIELDS = [
  ["public_name", "Public name"],
  ["tagline", "Tagline"],
  ["region_scope", "Region / area served"],
  ["contact_email", "Contact email"],
  ["membership_price", "Membership price"],
  ["join_cta", "Join button text"],
  ["public_host", "Public domain (host)"],
  ["linkedin_url", "LinkedIn URL"],
  ["youtube_url", "YouTube URL"],
  ["twitter_url", "X / Twitter URL"],
  ["eventbrite_url", "Eventbrite URL"],
  ["dues_amount", "Dues amount (e.g. £10 / year)"],
  ["dues_cycle", "Dues cycle: rolling or fixed"],
  ["dues_fixed_date", "Fixed renewal date (MM-DD)"],
  ["dues_payment_url", "Payment link (how to pay)"],
  ["dues_instructions", "Payment instructions"],
];

const CONTENT_LABELS = {
  home_intro: "Home page intro",
  about_body: "About page",
  membership_body: "Membership page",
};

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

export function WebsiteEditor({ chapterId, slug, canManage, isAdmin }) {
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState({});
  const [keys, setKeys] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [webhookInput, setWebhookInput] = useState("");
  const [webhookMsg, setWebhookMsg] = useState("");

  useEffect(() => {
    setSaved(false);
    api(`/api/chapters/${chapterId}/site`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setSettings(data.settings || { public_name: "", published: false });
        setContent(data.content || {});
        setKeys(data.content_keys || Object.keys(data.content || {}));
      });
  }, [chapterId]);

  function setField(key) {
    return (e) => setSettings({ ...settings, [key]: e.target.value });
  }
  function setBody(key) {
    return (e) => setContent({ ...content, [key]: e.target.value });
  }

  async function save() {
    setBusy(true);
    setSaved(false);
    // Only send webhook_url when the officer typed a new one, so leaving the
    // (write-only) field blank never wipes an existing webhook.
    const settingsPayload = { ...settings };
    if (webhookInput.trim()) settingsPayload.webhook_url = webhookInput.trim();
    const res = await api(`/api/chapters/${chapterId}/site`, {
      method: "PUT",
      body: JSON.stringify({ settings: settingsPayload, content }),
    });
    setBusy(false);
    if (res.ok) {
      const data = await res.json();
      setSettings(data.settings);
      setContent(data.content);
      setWebhookInput("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } else {
      const err = (await res.json().catch(() => ({}))).error;
      if (err) setWebhookMsg(err);
    }
  }

  async function removeWebhook() {
    const res = await api(`/api/chapters/${chapterId}/site`, {
      method: "PUT",
      body: JSON.stringify({ settings: { ...settings, webhook_url: "" }, content }),
    });
    if (res.ok) {
      setSettings((await res.json()).settings);
      setWebhookInput("");
      setWebhookMsg("");
    }
  }

  async function testWebhook() {
    setWebhookMsg("Sending…");
    const res = await api(`/api/chapters/${chapterId}/site/webhook-test`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    setWebhookMsg(res.ok && data.ok ? "Test message sent — check your channel ✓" : data.error || "Send failed — check the URL.");
  }

  if (settings === null) return <p className="muted">Loading website…</p>;

  return (
    <>
      <div className="section-head">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a className="btn-ghost" style={{ padding: "6px 12px", borderRadius: 8, textDecoration: "none" }} href={`/c/${slug}/`} target="_blank" rel="noreferrer">
            View public site ↗
          </a>
          <span className="pill">{settings.published ? "Published" : "Not published"}</span>
        </div>
        {canManage && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {saved && <span className="saved">Saved</span>}
            <button className="btn-primary" style={{ width: "auto" }} onClick={save} type="button" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>

      <div className="section">
        <div className="section-head"><h3>Site settings</h3></div>
        <div className="form-card">
          <div className="form-grid">
            {SETTINGS_FIELDS.map(([key, label]) => (
              <label className="field" key={key}>
                {label}
                <input
                  name={key}
                  value={settings[key] || ""}
                  onChange={setField(key)}
                  disabled={!canManage}
                />
              </label>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={!!settings.published}
              disabled={!canManage}
              onChange={(e) => setSettings({ ...settings, published: e.target.checked })}
            />
            Published (visible to the public)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={!!settings.dues_enabled}
              disabled={!canManage}
              onChange={(e) => setSettings({ ...settings, dues_enabled: e.target.checked })}
            />
            Dues tracking enabled
          </label>
        </div>
      </div>

      <div className="section">
        <div className="section-head"><h3>Security policy</h3></div>
        <div className="form-card">
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={!!settings.require_2fa}
              disabled={!isAdmin}
              onChange={(e) => setSettings({ ...settings, require_2fa: e.target.checked })}
            />
            Require two-factor authentication for committee accounts
            {!isAdmin && <span className="muted" style={{ fontSize: 12 }}>(admin only)</span>}
          </label>
          <p className="muted" style={{ fontSize: 13, margin: "8px 0 0" }}>
            When on, everyone with a committee role in this chapter must set up 2FA before they can
            use the admin cockpit. They'll be prompted to enrol at next sign-in.
          </p>
        </div>
      </div>

      <div className="section">
        <div className="section-head"><h3>Committee notifications</h3></div>
        <div className="form-card">
          <p className="muted" style={{ fontSize: 14, marginTop: 0 }}>
            Post new joins, RSVPs, enquiries, volunteer sign-ups and the daily digest to a
            Slack or Discord channel. Paste an <strong>incoming webhook URL</strong> — in Slack:
            Apps → Incoming Webhooks; in Discord: Channel settings → Integrations → Webhooks.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            {settings.webhook_set ? (
              <span className="pill" style={{ background: "var(--green-tint)", color: "var(--green)" }}>
                ✓ Connected{settings.webhook_platform ? ` · ${settings.webhook_platform}` : ""}
              </span>
            ) : (
              <span className="pill">Not configured</span>
            )}
            {canManage && settings.webhook_set && (
              <>
                <button className="btn-ghost" type="button" onClick={testWebhook}>Send test</button>
                <button className="btn-danger" type="button" onClick={removeWebhook}>Remove</button>
              </>
            )}
          </div>
          {canManage && (
            <label className="field">
              {settings.webhook_set ? "Replace webhook URL" : "Webhook URL"}
              <input
                name="webhook_url"
                type="url"
                autoComplete="off"
                placeholder="https://hooks.slack.com/services/…  or  https://discord.com/api/webhooks/…"
                value={webhookInput}
                onChange={(e) => setWebhookInput(e.target.value)}
              />
            </label>
          )}
          {webhookMsg && <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>{webhookMsg}</div>}
        </div>
      </div>

      <div className="section">
        <div className="section-head"><h3>Page content</h3></div>
        {keys.map((key) => (
          <label className="field" key={key} style={{ marginBottom: 16 }}>
            {CONTENT_LABELS[key] || key}
            <textarea value={content[key] || ""} onChange={setBody(key)} disabled={!canManage} />
          </label>
        ))}
      </div>
    </>
  );
}
